/* ==========================================================================
   LINDIE Spotlight — the "Only from LINDIE" cover carousel.

   Covers are stacked at the centre of the stage and pushed outwards with
   per-element custom properties, so nothing reflows as the selection moves and
   the whole transition stays on the compositor. Anything more than two places
   from the active cover is parked off-stage and made inert.

   Beyond the prototype, this also keeps the real Add to Cart and Preview hooks
   in step with the active title: the variant id on the hidden input and the
   isbn/title on the preview button are rewritten as the carousel moves, so the
   delegated handlers in lindie-cart.js and lindie-audio-preview.js — which read
   those attributes at click time — act on the right product.

   Namespaced .lsp-* / data-lsp-*. Scoped per section, so two on one page do
   not interfere.
   ========================================================================== */
(function () {
  'use strict';

  /* Distance-indexed placement: [active, one out, two out].
     Narrow viewports get a tighter spread so the side covers stay on screen. */
  function steps() {
    var narrow = window.matchMedia('(max-width: 749px)').matches;
    return narrow
      ? [{ x: 0, s: 1, o: 1 }, { x: 122, s: 0.68, o: 0.85 }, { x: 205, s: 0.54, o: 0.45 }]
      : [{ x: 0, s: 1, o: 1 }, { x: 232, s: 0.64, o: 0.9 }, { x: 396, s: 0.52, o: 0.5 }];
  }

  function setupStage(root) {
    var stage = root.querySelector('[data-lsp-stage]');
    if (!stage || root.dataset.lspBound === 'true') return;

    var items = [].slice.call(stage.querySelectorAll('[data-lsp-item]'));
    if (!items.length) return;

    root.dataset.lspBound = 'true';

    var dots = [].slice.call(root.querySelectorAll('[data-lsp-dot]'));
    var prevBtn = stage.querySelector('[data-lsp-dir="-1"]');
    var nextBtn = stage.querySelector('[data-lsp-dir="1"]');

    var titleEl = root.querySelector('[data-lsp-meta-title]');
    var authorEl = root.querySelector('[data-lsp-meta-author]');
    var priceEl = root.querySelector('[data-lsp-meta-price]');
    var linkEl = root.querySelector('[data-lsp-meta-link]');
    /* NOT '[data-lsp-variant]' — every cover carries that, so this would match
       the first cover button and the form would post an empty id. */
    var variantEl = root.querySelector('[data-lsp-variant-input]');
    var atcBtn = root.querySelector('[data-lsp-atc]');
    var previewBtn = root.querySelector('[data-lsp-preview]');

    /* Start on the middle cover so both sides of the fan are visible. */
    var active = Math.floor((items.length - 1) / 2);

    function render() {
      var step = steps();

      items.forEach(function (el, i) {
        var offset = i - active;
        var dist = Math.abs(offset);
        var beyond = dist > 2;
        var cfg = step[Math.min(dist, 2)];
        var dir = offset < 0 ? -1 : 1;

        el.style.setProperty('--x', (beyond ? dir * 460 : dir * cfg.x) + 'px');
        el.style.setProperty('--s', beyond ? 0.4 : cfg.s);
        el.style.setProperty('--o', beyond ? 0 : cfg.o);
        el.style.setProperty('--z', String(10 - dist));
        el.classList.toggle('lsp__item--active', offset === 0);
        el.setAttribute('aria-hidden', beyond ? 'true' : 'false');
        el.tabIndex = beyond ? -1 : 0;
      });

      var cur = items[active];
      var d = cur.dataset;

      if (titleEl) titleEl.textContent = d.lspItemTitle || '';
      if (authorEl) {
        authorEl.textContent = d.lspAuthor || '';
        authorEl.hidden = !d.lspAuthor;
      }
      if (priceEl) priceEl.textContent = d.lspPrice || '';
      if (linkEl) linkEl.setAttribute('href', d.lspHref || '#');

      /* Keep the cart and preview hooks pointing at the active title. Both
         handlers are delegated and read these at click time, so rewriting them
         is enough — nothing needs rebinding. */
      if (variantEl) variantEl.value = d.lspVariant || '';

      if (atcBtn) {
        var sold = d.lspAvailable !== 'true';

        /* No fallback text. The button has two states with their own labels, and
           clearing either one in the theme editor is how that state is hidden —
           an icon on its own says nothing. The Liquid drops the whole form when
           the Add label is empty; this covers the sold-out label, which only
           matters once a title actually is sold out. */
        var text = sold ? (atcBtn.dataset.lspSoldLabel || '')
                        : (atcBtn.dataset.lspAddLabel || '');

        atcBtn.hidden = !text;
        atcBtn.disabled = sold;
        atcBtn.classList.toggle('lsp__btn--sold-out', sold);
        atcBtn.setAttribute('aria-label',
          (sold ? 'Sold out: ' : 'Add to cart: ') + (d.lspItemTitle || ''));

        var label = atcBtn.querySelector('[data-lsp-atc-label]');
        if (label) label.textContent = text;
      }

      if (previewBtn) {
        previewBtn.setAttribute('data-isbn', d.lspIsbn || '');
        previewBtn.setAttribute('data-title', d.lspItemTitle || '');
        /* No sample to play without an ISBN, so the control is withdrawn
           rather than left to fail silently. */
        previewBtn.hidden = !d.lspIsbn;
      }

      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', String(i === active));
      });

      /* Clamped rather than wrapped: the ends should feel like ends. */
      if (prevBtn) prevBtn.disabled = active === 0;
      if (nextBtn) nextBtn.disabled = active === items.length - 1;
    }

    function go(i) {
      active = Math.max(0, Math.min(items.length - 1, i));
      render();
    }

    stage.querySelectorAll('[data-lsp-dir]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        go(active + Number(btn.getAttribute('data-lsp-dir')));
      });
    });

    items.forEach(function (el, i) {
      el.addEventListener('click', function () { go(i); });
    });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); });
    });

    stage.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); go(active - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); go(active + 1); }
    });

    /* The spread changes at the 749px breakpoint. */
    window.addEventListener('resize', render, { passive: true });

    render();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-lsp-section]').forEach(setupStage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  /* Theme editor re-renders the section; setupStage is idempotent per section. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
